<script setup lang="ts">

import NamaIcon from "@/components/icons/NamaIcon.vue";
import Translator from "@/utils/common/Translator.ts";
import ChatPresenter from "@/utils/presenters/ChatPresenter.ts";
import {computed, onBeforeUnmount, ref} from "vue";
import {ChatAttachment} from "@/utils/presenters/interfaces.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import axios from "axios";

const props = defineProps<{
  presenter: ChatPresenter;
}>();

const isRecording = ref(false);
const recordingDuration = ref(0);
const recordingSending = ref(false);
let mediaRecorder: MediaRecorder | null = null;
let recordedChunks: Blob[] = [];
let recordingTimer: ReturnType<typeof setInterval> | null = null;
let audioStream: MediaStream | null = null;
let microphoneAccessGranted = false;

const formattedDuration = computed(() => {
  const m = Math.floor(recordingDuration.value / 60);
  const s = recordingDuration.value % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
});

async function askForMicrophoneAccess(): Promise<boolean> {
  if (location.protocol === "http:" && location.hostname !== "localhost" && !location.hostname.startsWith("127.0.0.")) {
    useErrorsStore().pushErrorStr("Microphone access is blocked over HTTP. Please use HTTPS or localhost.");
    return false;
  }
  try {
    const perm = await navigator.permissions.query({name: "microphone" as PermissionName});
    if (perm.state === "granted") {
      microphoneAccessGranted = true;
      return true;
    }
    const stream = await navigator.mediaDevices.getUserMedia({audio: true});
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      microphoneAccessGranted = true;
      return true;
    }
  } catch (e) {
    console.error('Microphone access failed:', e);
    useErrorsStore().pushErrorStr("Microphone access failed");
  }
  return false;
}

async function startRecording() {
  if (!microphoneAccessGranted) {
    const granted = await askForMicrophoneAccess();
    if (!granted)
      return;
  }
  try {
    audioStream = await navigator.mediaDevices.getUserMedia({audio: true});
    recordedChunks = [];
    recordingDuration.value = 0;
    let mimeType = '';
    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus'))
      mimeType = 'audio/webm;codecs=opus'
    if (MediaRecorder.isTypeSupported('audio/webm'))
      mimeType = 'audio/webm';
    mediaRecorder = new MediaRecorder(audioStream, mimeType ? {mimeType} : undefined);
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) recordedChunks.push(e.data);
    };
    mediaRecorder.start(100);
    isRecording.value = true;
    recordingTimer = setInterval(() => {
      recordingDuration.value++;
    }, 1000);
  } catch (err) {
    console.error('Microphone access denied:', err);
    useErrorsStore().pushErrorStr("Microphone access failed");
  }
}

function stopMediaRecorder(): Promise<Blob> {
  return new Promise((resolve) => {
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      resolve(new Blob());
      return;
    }
    mediaRecorder.onstop = () => {
      const ext = mediaRecorder!.mimeType.includes('webm') ? 'webm' : 'ogg';
      const blob = new Blob(recordedChunks, {type: mediaRecorder!.mimeType || `audio/${ext}`});
      resolve(blob);
    };
    mediaRecorder.stop();
  });
}

function cleanupRecording() {
  if (recordingTimer) {
    clearInterval(recordingTimer);
    recordingTimer = null;
  }
  if (audioStream) {
    audioStream.getTracks().forEach(t => t.stop());
    audioStream = null;
  }
  mediaRecorder = null;
  recordedChunks = [];
  isRecording.value = false;
  recordingDuration.value = 0;
  recordingSending.value = false;
}

function cancelRecording() {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.onstop = null;
    mediaRecorder.stop();
  }
  cleanupRecording();
}

async function sendRecording() {
  if (!mediaRecorder || mediaRecorder.state === 'inactive') {
    cleanupRecording();
    return;
  }
  recordingSending.value = true;
  const blob = await stopMediaRecorder();
  if (blob.size === 0) {
    cleanupRecording();
    return;
  }
  try {
    const ext = blob.type.includes('webm') ? 'opus' : 'ogg';
    const fileName = `voice-${Date.now()}.${ext}`;
    const formData = new FormData();
    formData.append('file', blob, fileName);
    const uploadResponse = await axios.post('upload', formData);
    const response = uploadResponse.data;
    if (response?.fileId) {
      const attachment: ChatAttachment = {
        fileId: response.fileId,
        fileName: fileName,
        fileSize: blob.size
      };
      await props.presenter.sendMessage('', {
        attachmentInfo: {fileId: attachment.fileId, fileName: attachment.fileName, updated: true, removed: false},
        dataLength: attachment.fileSize
      });
    }
  } catch (err) {
    console.error('Failed to upload voice message:', err);
  } finally {
    cleanupRecording();
  }
}

onBeforeUnmount(() => {
  if (isRecording.value)
    cancelRecording();
});

defineExpose({startRecording, isRecording, recordingSending});
</script>

<template>
  <div v-if="isRecording" class="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-w-full">
    <q-btn round dense flat icon="delete" color="negative" @click="cancelRecording"
           :title="Translator.translateId('cancel')"/>
    <div class="tw-flex tw-items-center tw-gap-2 tw-flex-1">
      <div class="tw-w-3 tw-h-3 tw-rounded-full tw-bg-red-500 tw-animate-pulse"/>
      <span class="tw-text-sm tw-font-mono tw-tabular-nums tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark"
            v-text="formattedDuration"/>
    </div>
    <q-btn round dense flat color="primary" @click="sendRecording"
           :title="Translator.translateId('send')" :disable="recordingSending">
      <NamaIcon defaultIcon="nm-send" class="tw-size-5" :class="[Translator.isLanguageRTL()?'tw-rotate-180':'']"/>
    </q-btn>
  </div>
</template>
